import React, { useEffect, useState } from "react";
import { store } from "../../utils/store";
import YhTable, { YhTableColumn, YhTableCellParams } from "../../ui/YhTable";
import { getBooking } from "./BookingService";
import { useSelector } from "react-redux";
import { selectActiveOrganizationId } from "../../redux/authSlice";
import { formatPhoneKeepingCountryCode } from "../../Shared/Phone/PhoneWithCountryCode";
import BookingDetailTray from "./BookingDetailTray";

const statusStyles: Record<string, React.CSSProperties> = {
  confirmed: {
    backgroundColor: "#E6F4EA",
    color: "#1E7F43",
  },
  cancelled: {
    backgroundColor: "#FDECEA",
    color: "#D32F2F",
  },
  rescheduled: {
    backgroundColor: "#FFF4E5",
    color: "#ED6C02",
  },
  completed: {
    backgroundColor: "#E3F2FD",
    color: "#1976D2",
  },
};

const BookingList = () => {
  const [tableData, setTableData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [total, setTotal] = useState(0);
  const [selectedBooking, setSelectedBooking] = useState<any | null>(null);
  const activeOrganizationId = useSelector(selectActiveOrganizationId);

  useEffect(() => {
    setPage(0);
  }, [activeOrganizationId]);

  useEffect(() => {
    handleTableData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, activeOrganizationId]);

  const columns: YhTableColumn[] = [
    {
      headerName: "Project",
      field: "project",
      minWidth: 200,
      flex: 1,
      renderCell: (params: YhTableCellParams) => (
        <div className="flex flex-col items-start gap-0.5">
          <span className="text-[13px] font-semibold capitalize text-[#111827]">
            {params.row?.projectDetailId?.projectId?.project_name}
          </span>
          <span className="text-[13px] text-[#374151]">
            {params.row?.projectDetailId?.location}
          </span>
          <span className="text-[13px] text-[#374151]">
            {params.row?.projectDetailId?.propertyPurpose}
          </span>
        </div>
      ),
    },
    {
      headerName: "Meeting Host",
      field: "name",
      minWidth: 160,
      flex: 1,
      renderCell: (params: YhTableCellParams) => (
        <span className="text-[13px] font-semibold capitalize text-[#111827]">
          {params.row?.meetingId?.host?.name}
        </span>
      ),
    },
    {
      headerName: "User Details",
      field: "user",
      minWidth: 200,
      flex: 1,
      renderCell: (params: YhTableCellParams) => (
        <div className="flex flex-col items-start gap-0.5">
          <span className="text-[13px] font-semibold capitalize text-[#111827]">
            {params.row?.userData?.name}
          </span>
          <span className="text-[13px] text-[#374151]">
            {params.row?.userData?.email}
          </span>
          <span className="font-mono text-[13px] tracking-wide text-[#374151]">
            {formatPhoneKeepingCountryCode(params.row?.userData?.phone)}
          </span>
        </div>
      ),
    },
    {
      headerName: "Date & Slot",
      field: "slot",
      minWidth: 160,
      flex: 1,
      renderCell: (params: YhTableCellParams) => (
        <div className="flex flex-col items-start gap-0.5">
          <span className="text-[13px] font-semibold text-[#111827]">
            {params.row?.selectedDate
              ? new Date(params.row.selectedDate).toLocaleDateString()
              : "—"}
          </span>
          <span className="text-[13px] text-[#374151]">
            {params.row?.selectedSlot || "—"}
          </span>
        </div>
      ),
    },
    {
      headerName: "Type",
      field: "appointmentType",
      minWidth: 110,
      renderCell: (params: YhTableCellParams) => (
        <span className="text-[13px] capitalize text-[#374151]">
          {params.row?.appointmentType || "—"}
        </span>
      ),
    },
    {
      headerName: "Status",
      field: "status",
      minWidth: 120,
      renderCell: (params: YhTableCellParams) => {
        const status = params.row.status?.toLowerCase?.() || "";
        return (
          <span
            className="inline-block w-fit rounded-xl px-3 py-1.5 text-xs font-semibold capitalize"
            style={statusStyles[status]}
          >
            {params.row.status}
          </span>
        );
      },
    },
  ];

  const handleTableData = async () => {
    try {
      setLoading(true);
      const response = await getBooking({
        userId: store.getState().auth.userId,
        page,
        limit: pageSize,
        // Always show only the logged-in host's bookings.
        // Org scope comes from header OrgSwitcher (x-organization-id).
        scope: "mine",
      });
      setTableData(response?.data || []);
      setTotal(response?.total || 0);
      if (selectedBooking?._id) {
        const refreshed = (response?.data || []).find(
          (b: any) => String(b._id) === String(selectedBooking._id)
        );
        if (refreshed) setSelectedBooking(refreshed);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-[calc(100vh-68px)] max-h-[calc(100vh-68px)] gap-4 overflow-hidden bg-white p-4 sm:p-5 lg:p-6">
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <div className="mb-4 flex shrink-0 flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-[#111827]">
              Booking List
            </h1>
            <p className="mt-1 text-sm text-[#6B7280]">
              Showing only bookings for meetings you host. Click a row for
              details, Join Meet, or IVR call.
            </p>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-auto">
          <YhTable
            columns={columns}
            rows={tableData}
            getRowId={(row) => row._id}
            rowHeight={110}
            loading={loading}
            emptyMessage="No bookings on your meetings yet"
            className="min-h-[320px]"
            page={page}
            pageSize={pageSize}
            totalRows={total}
            onPageChange={setPage}
            onPageSizeChange={(s) => {
              setPageSize(s);
              setPage(0);
            }}
            onRowClick={(row) => setSelectedBooking(row)}
          />
        </div>
      </div>

      <aside
        aria-hidden={!selectedBooking}
        className={`relative h-full shrink-0 overflow-hidden transition-[width,opacity] duration-300 ease-in-out ${
          selectedBooking
            ? "w-[280px] opacity-100 sm:w-[300px] xl:w-[340px]"
            : "pointer-events-none w-0 opacity-0"
        }`}
      >
        <div
          className={`relative flex h-full flex-col overflow-hidden rounded-2xl border border-[#EEF0F5] bg-white shadow-[0_4px_20px_rgba(15,23,42,0.06)] transition-transform duration-300 ease-in-out ${
            selectedBooking ? "translate-x-0" : "translate-x-full"
          }`}
          style={{ width: "100%" }}
        >
          {selectedBooking ? (
            <BookingDetailTray
              booking={selectedBooking}
              onClose={() => setSelectedBooking(null)}
            />
          ) : null}
        </div>
      </aside>
    </div>
  );
};

export default BookingList;
